import Card from '@components/Card/Card';
import MobileCardPreview from '@components/Card/MobileCardPreview/MobileCardPreview';
import { StaticCard } from '@components/Card/StaticCard/StaticCard';
import { onboardingExampleCard } from '@components/Card/StaticCard/static-example-cards';
import type { SettableNode } from '@hooks/use-node';
import { AnimatePresence, motion } from 'motion/react';

const USER_CARD_ANIMATION_DURATION_MS = 200;
const EXAMPLE_CARD_ANIMATION_DURATION_MS = 200;

interface CustomizeCardPreviewProps {
  startedEditing: boolean;
  loading: boolean;
  cardHeadingNode: SettableNode<HTMLHeadingElement>;
  cardRef: (instance: HTMLElement | null) => void;
}

// Client-only — imported via next/dynamic with ssr:false. Reads card state
// from the parent's cardContext.Provider, so the parent's cache hydration is
// already reflected by the time this component first mounts.
export const CustomizeCardPreview: React.FC<CustomizeCardPreviewProps> = ({
  startedEditing,
  loading,
  cardHeadingNode,
  cardRef,
}) => (
  <AnimatePresence mode="wait" initial={false}>
    {startedEditing ? (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{
          duration: USER_CARD_ANIMATION_DURATION_MS / 1000,
          origin: 1,
          ease: 'easeOut',
        }}
        key="edit-card"
      >
        <MobileCardPreview loading={loading}>
          <Card
            headingNode={cardHeadingNode}
            editMode={startedEditing}
            loading={loading}
            forwardedRef={cardRef}
            mode="fit"
          />
        </MobileCardPreview>
      </motion.div>
    ) : (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{
          opacity: 0,
          scale: 0.95,
          transition: { duration: EXAMPLE_CARD_ANIMATION_DURATION_MS / 1000 },
        }}
        transition={{
          duration: EXAMPLE_CARD_ANIMATION_DURATION_MS / 1000,
          origin: 1,
          ease: 'easeOut',
        }}
        key="example-card"
      >
        <MobileCardPreview loading={loading}>
          <StaticCard exampleCard={onboardingExampleCard} loading={loading} mode="fit" />
        </MobileCardPreview>
      </motion.div>
    )}
  </AnimatePresence>
);

// Static fallback used by next/dynamic while the CustomizeCardPreview chunk
// loads. SSR-renders cleanly since StaticCard with loading=true renders only
// CardLoader (no client-only Card component).
export const CustomizeCardPreviewShimmer: React.FC = () => (
  <MobileCardPreview loading={true}>
    <StaticCard exampleCard={onboardingExampleCard} loading={true} mode="fit" />
  </MobileCardPreview>
);

export default CustomizeCardPreview;
